import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import { animeList, weekDays } from "@/mocks/anime";

const catOptions = ["全部", "热血", "奇幻", "恋爱", "悬疑", "搞笑", "日常", "科幻"];

export default function Series() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") ?? "全部";
  const q = (params.get("q") ?? "").trim();

  const list = useMemo(() => {
    let result = animeList;
    if (cat !== "全部") result = result.filter((a) => a.category === cat);
    if (q) result = result.filter((a) => a.title.includes(q) || a.tags.some((t) => t.includes(q)));
    return result;
  }, [cat, q]);

  const setCat = (c: string) => {
    if (c === "全部") {
      params.delete("cat");
    } else {
      params.set("cat", c);
    }
    setParams(params, { replace: true });
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: cat === "全部" ? "妖精动漫网本周新番连载动漫" : `${cat}动漫连载`,
    url: `https://comicnot.github.io/series${q ? `?q=${encodeURIComponent(q)}` : cat !== "全部" ? `?cat=${encodeURIComponent(cat)}` : ""}`,
    inLanguage: "zh-CN",
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: list.length,
      itemListElement: list.map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://comicnot.github.io/anime/${a.id}`,
        name: a.title,
      })),
    },
  };

  return (
    <>
      <Header />
      <main className="bg-background-50">
        <section className="bg-background-100 py-8 md:py-12">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <nav aria-label="面包屑" className="text-xs text-foreground-600">
              <ol className="flex flex-wrap items-center gap-1.5">
                <li>
                  <Link to="/" className="hover:text-primary-600">首页</Link>
                </li>
                <li>/</li>
                <li aria-current="page" className="text-foreground-500">本周新番</li>
              </ol>
            </nav>
            <h1 className="mt-3 text-2xl font-bold text-foreground-950 md:text-3xl">
              本周新番 · 连载动漫
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              汇总本周正在更新的新番与连载动漫，分类清晰、更新及时。点开任意作品即可查看最新一话，追更体验直接拉满。
            </p>
          </div>
        </section>

        <section className="py-8 md:py-10">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="flex flex-wrap items-center gap-2">
              {catOptions.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCat(c)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    cat === c
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            {q && (
              <p className="mt-4 text-sm text-foreground-600">
                搜索“<span className="font-bold text-foreground-900">{q}</span>”共找到 {list.length} 部作品，
                <Link to="/series" className="text-primary-600 hover:underline">清除搜索</Link>
              </p>
            )}

            {list.length > 0 ? (
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {list.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            ) : (
              <div className="mt-10 flex flex-col items-center py-16 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-2-line text-3xl"></i>
                </span>
                <p className="mt-4 font-bold text-foreground-900">暂时没有找到相关作品</p>
                <p className="mt-1 text-sm text-foreground-600">换个分类或关键词试试吧</p>
              </div>
            )}
          </div>
        </section>

        <section className="bg-background-100 py-8 md:py-10">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <h2 className="text-xl font-bold text-foreground-950">一周更新安排</h2>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
              {weekDays.map((day) => {
                const count = animeList.filter((a) => a.day === day).length;
                return (
                  <Link
                    key={day}
                    to={`/calendar#${day}`}
                    className="rounded-lg border border-background-200 bg-background-50 p-3 text-center transition-colors hover:border-primary-200"
                  >
                    <p className="text-sm font-bold text-foreground-950">{day}</p>
                    <p className="mt-1 text-xs text-primary-600">{count} 部更新</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </>
  );
}